// Evidence overrides on a letter paragraph (TDI-406).
//
// In the "What this paragraph was written from" panel a clinician can remove
// a piece of evidence or mark it more or less relevant, then have the
// paragraph redrafted from the adjusted evidence. A piece is one non-empty
// line of one input field's value (the server writes one answer, tag or
// score per line); a single-line value is one piece. The choice is stored at
// sections.{letterType}.evidenceOverrides[code][pieceKey] = { state, text },
// the text being the line as it was when the choice was made, so the server
// can tell a stale choice from a live one.
//
// Mirrors split_evidence_lines / evidence_piece_key / EVIDENCE_OVERRIDE_STATES
// in python-functions/rms_functions/rms_llm_inputs.py.

export const EVIDENCE_OVERRIDE_REMOVED = 'removed';
export const EVIDENCE_OVERRIDE_LESS = 'less';
export const EVIDENCE_OVERRIDE_MORE = 'more';
export const EVIDENCE_OVERRIDE_STATES = [
  EVIDENCE_OVERRIDE_REMOVED, EVIDENCE_OVERRIDE_LESS, EVIDENCE_OVERRIDE_MORE,
];

export const EVIDENCE_OVERRIDE_LABELS = {
  [EVIDENCE_OVERRIDE_REMOVED]: 'Removed',
  [EVIDENCE_OVERRIDE_LESS]: 'Less relevant',
  [EVIDENCE_OVERRIDE_MORE]: 'More relevant',
};

/** The pieces of one field value: its non-empty lines, trimmed, in order. */
export function splitEvidenceLines(value) {
  if (typeof value !== 'string') return [];
  return value.split('\n').map((line) => line.trim()).filter(Boolean);
}

export function evidencePieceKey(category, fieldKey, index) {
  return `${category}|${fieldKey}|${index}`;
}

/** The state recorded for a piece, or null. */
export function overrideState(overrides, key) {
  const entry = overrides && typeof overrides === 'object' ? overrides[key] : null;
  const state = entry && typeof entry === 'object' ? entry.state : null;
  return EVIDENCE_OVERRIDE_STATES.includes(state) ? state : null;
}

/** Counts per state, plus the total, for one paragraph's overrides. */
export function summariseOverrides(overrides) {
  const summary = { removed: 0, less: 0, more: 0, total: 0 };
  if (!overrides || typeof overrides !== 'object') return summary;
  Object.keys(overrides).forEach((key) => {
    const state = overrideState(overrides, key);
    if (!state) return;
    summary[state] += 1;
    summary.total += 1;
  });
  return summary;
}

/** "2 removed, 1 marked less relevant" for the panel and the history. */
export function describeOverrides(overrides) {
  const summary = summariseOverrides(overrides);
  const parts = [];
  if (summary.removed) parts.push(`${summary.removed} removed`);
  if (summary.less) parts.push(`${summary.less} marked less relevant`);
  if (summary.more) parts.push(`${summary.more} marked more relevant`);
  return parts.join(', ');
}
